import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { login, verify2fa, saveToken, formatApiError } from "@/lib/api";
import { ADMIN_BASE, ADMIN_MDP } from "@/lib/adminPath";

const inputCls =
  "w-full rounded-sm border border-espresso-border bg-espresso-2 px-4 py-3.5 text-sm text-ivory outline-none transition-colors duration-200 placeholder:text-ivory/30 focus:border-primary focus:ring-1 focus:ring-primary";

export default function AdminLogin() {
  const [etape, setEtape] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [erreur, setErreur] = useState("");
  const [chargement, setChargement] = useState(false);
  const navigate = useNavigate();

  const soumettreMotDePasse = async (e) => {
    e.preventDefault();
    setErreur("");
    setChargement(true);
    try {
      await login(email, password);
      setEtape(2);
    } catch (err) {
      setErreur(formatApiError(err.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  };

  const soumettreCode = async (e) => {
    e.preventDefault();
    setErreur("");
    setChargement(true);
    try {
      const data = await verify2fa(email, code);
      saveToken(data.token);
      navigate(data.user?.must_change_password ? ADMIN_MDP : ADMIN_BASE);
    } catch (err) {
      setErreur(formatApiError(err.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  };

  return (
    <section className="flex min-h-[85vh] items-center justify-center bg-espresso px-6 py-20" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-espresso-border bg-espresso-2 p-10">
        <div className="flex items-center gap-4">
          <img src="/logo-hora-dark.png" alt="Fondation HORA" className="h-12 w-auto" data-testid="admin-login-logo" />
          <span className="border-l border-espresso-border pl-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
            Espace admin
          </span>
        </div>
        <p className="mt-4 text-sm text-ivory/50">Réservé à l'équipe de la Fondation Hora.</p>

        {etape === 1 ? (
          <form onSubmit={soumettreMotDePasse} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">Courriel</label>
              <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="info@fondationhora.org" data-testid="admin-login-email-input" />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">Mot de passe</label>
              <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="admin-login-password-input" />
            </div>
            {erreur && (
              <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">{erreur}</p>
            )}
            <button
              type="submit"
              disabled={chargement}
              data-testid="admin-login-submit-button"
              className="flex w-full items-center justify-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,opacity] duration-200 hover:bg-[hsl(22,83%,40%)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Lock size={16} />
              {chargement ? "Vérification…" : "Continuer"}
            </button>
          </form>
        ) : (
          <form onSubmit={soumettreCode} className="mt-8 space-y-5" data-testid="admin-2fa-form">
            <p className="border border-gold/30 bg-gold/10 px-4 py-3 text-sm leading-relaxed text-ivory/80" data-testid="admin-2fa-message">
              Un code de vérification à 6 chiffres vient d'être envoyé à <strong className="text-gold">{email}</strong>. Il expire dans 10 minutes.
            </p>
            <div>
              <label htmlFor="admin-2fa-code" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">Code de vérification</label>
              <input
                id="admin-2fa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className={`${inputCls} text-center text-2xl font-semibold tracking-[0.5em]`}
                placeholder="------"
                data-testid="admin-2fa-code-input"
              />
            </div>
            {erreur && (
              <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-red-300" data-testid="admin-2fa-error">{erreur}</p>
            )}
            <button
              type="submit"
              disabled={chargement || code.length !== 6}
              data-testid="admin-2fa-submit-button"
              className="flex w-full items-center justify-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,opacity] duration-200 hover:bg-[hsl(22,83%,40%)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ShieldCheck size={16} />
              {chargement ? "Vérification…" : "Vérifier et se connecter"}
            </button>
            <button
              type="button"
              onClick={() => { setEtape(1); setCode(""); setErreur(""); }}
              data-testid="admin-2fa-back-button"
              className="flex w-full items-center justify-center gap-2 text-xs text-ivory/40 transition-colors hover:text-gold"
            >
              <ArrowLeft size={14} />
              Recommencer — un nouveau code sera envoyé
            </button>
          </form>
        )}

        <Link to="/" className="mt-8 block text-center text-xs text-ivory/40 transition-colors hover:text-gold" data-testid="admin-login-back-link">
          Retour au site public
        </Link>
      </div>
    </section>
  );
}
